import { Fragment, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  api,
  qs,
  regionUrl,
  type ClippingProject,
  type ClippingRow,
  type LicenceRow,
  type SavedSearchRow,
  type UnitType,
} from '../lib/api'
import { useSession } from '../lib/session'
import { Chevron, CropPlaceholder, ErrorNote, TypeBadge } from '../components/Page'
import {
  AlertRadios,
  EmptyClippings,
  EmptySavedSearches,
  ErrorCard,
  type AlertFrequency,
} from '../components/reader/states'
import {
  CITATION_STYLES,
  isServerRendered,
  recordsToCsv,
  renderStyle,
  STYLE_NOTE,
  type CitationRecord,
  type CitationStyle,
} from '../components/reader/citations'

/**
 * Board 05.2: clippings, organised into projects, and 05.5: saved searches.
 *
 * The load-bearing fact on this screen is that a clipping freezes its citation
 * at creation. Enrichment keeps running over the archive underneath, so what a
 * researcher cited in March has to still be what they cited in September. The
 * footnote is therefore stored, not derived, and this screen renders the
 * stored record rather than looking anything up again.
 */

/** The row, plus the fields `api.ts` does not carry yet. */
interface ClippingRowPlus extends ClippingRow {
  confidence?: number | null
  /** The application's own crop URL, licence-checked, as the API returns it. */
  crop_url?: string | null
  unit_id?: string | null
}

const UNFILED = 'unfiled'

export function ClippingsPage() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { config, can } = useSession()
  const productName = config?.product_name ?? 'this archive'

  const projects = useQuery({
    queryKey: ['clipping-projects'],
    queryFn: () => api.get<ClippingProject[]>('/clipping-projects'),
  })

  const clippings = useQuery({
    queryKey: ['clippings', 'all'],
    queryFn: () => api.get<ClippingRowPlus[]>('/clippings'),
  })

  const licences = useQuery({
    queryKey: ['licences', 'mine'],
    queryFn: () => api.get<LicenceRow[]>('/licences/mine'),
    retry: false,
  })

  const createProject = useMutation({
    mutationFn: (name: string) => api.post<ClippingProject>('/clipping-projects', { name }),
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: ['clipping-projects'] })
      navigate(`/clippings/${project.id}`)
    },
  })

  const all = useMemo(() => clippings.data ?? [], [clippings.data])
  const unfiled = useMemo(() => all.filter((row) => !row.project_id), [all])
  const shown = useMemo(() => {
    if (!projectId) return all
    if (projectId === UNFILED) return unfiled
    return all.filter((row) => row.project_id === projectId)
  }, [all, unfiled, projectId])

  const project = projects.data?.find((row) => row.id === projectId) ?? null
  const heading = project
    ? project.name
    : projectId === UNFILED
      ? 'Unfiled clippings'
      : 'All clippings'

  const updated = useMemo(() => {
    const newest = shown
      .map((row) => row.created_at)
      .sort()
      .pop()
    return newest ? relative(newest) : null
  }, [shown])

  const licence = licences.data?.[0] ?? null
  const exportRights = !!licence?.permits_export && can('content.export_bulk')
  const tdmRights = can('content.tdm')

  if (clippings.isError) {
    return (
      <div className="mx-auto w-full max-w-[560px] p-6">
        <ErrorCard
          title="Your clippings will not load."
          rows={[
            ['what', 'clippings'],
            ['stage', 'api clippings · error'],
            ['reason', clippings.error instanceof Error ? clippings.error.message : 'unknown'],
            ['at', new Date().toISOString().slice(0, 19).replace('T', ' ')],
          ]}
          survives="Nothing is lost: a clipping and its frozen citation live on the server, and this is only the list of them."
          actions={[
            { label: 'Try again', onClick: () => void clippings.refetch() },
            { label: 'Search instead', href: '/search' },
          ]}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0 lg:flex-row">
      <aside className="flex w-full flex-none flex-col border-b border-n300 bg-surface px-4 py-4 lg:w-[268px] lg:border-b-0 lg:border-r">
        <div className="mb-3 flex items-baseline justify-between">
          <div className="chrome">Projects</div>
          <button
            type="button"
            className="btn-ghost font-mono text-[10px] uppercase tracking-[0.06em]"
            onClick={() => {
              const name = window.prompt('Name this project')
              if (name) createProject.mutate(name)
            }}
          >
            New
          </button>
        </div>
        <ErrorNote error={createProject.error} />
        <nav className="flex flex-col gap-0.5">
          <ProjectRow
            to="/clippings"
            name="All clippings"
            count={all.length}
            selected={!projectId}
          />
          {projects.data?.map((row) => (
            <ProjectRow
              key={row.id}
              to={`/clippings/${row.id}`}
              name={row.name}
              count={
                all.length ? all.filter((clip) => clip.project_id === row.id).length : row.clipping_count
              }
              selected={projectId === row.id}
            />
          ))}
          {unfiled.length > 0 && (
            <ProjectRow
              to={`/clippings/${UNFILED}`}
              name="Unfiled"
              count={unfiled.length}
              selected={projectId === UNFILED}
            />
          )}
        </nav>
        <div className="mt-6 border-t border-n300 pt-3.5 font-mono text-[10px] uppercase leading-[1.8] text-ink2 lg:mt-auto">
          <div className="text-body">
            {all.length.toLocaleString()} clipping{all.length === 1 ? '' : 's'} ·{' '}
            {(projects.data?.length ?? 0).toLocaleString()} project
            {projects.data?.length === 1 ? '' : 's'}
          </div>
          <div>export rights: {exportRights ? 'granted' : 'not granted'}</div>
          <div>tdm rights: {tdmRights ? 'granted' : 'not granted'}</div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:min-h-0">
        <header className="flex-none border-b border-n300 px-5 pb-3.5 pt-4">
          <div className="flex flex-wrap items-end justify-between gap-5">
            <div>
              <div className="mb-[5px] font-mono text-[10.5px] uppercase tracking-[0.12em] text-ink2">
                {[
                  project ? 'project' : 'all clippings',
                  `${shown.length} clipping${shown.length === 1 ? '' : 's'}`,
                  updated ? `updated ${updated}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
              <h1 className="text-[27px] leading-[1.1]">{heading}</h1>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal"
                onClick={() => void navigator.clipboard?.writeText(window.location.href)}
                title="Copy a link to this project"
              >
                Share
              </button>
              <button
                type="button"
                className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal disabled:opacity-45"
                disabled={!exportRights || shown.length === 0}
                title={
                  exportRights
                    ? 'Take the frozen citation records away as CSV'
                    : 'Your licence does not permit export'
                }
                onClick={() =>
                  download(
                    `${slug(heading)}.csv`,
                    'text/csv',
                    recordsToCsv(shown.map((row) => row.citation as CitationRecord)),
                  )
                }
              >
                Export {shown.length}
              </button>
              <a
                href="#citation-export"
                className="btn-primary px-[13px] py-1.5 text-[13.5px] no-underline"
              >
                Cite all
              </a>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 px-5 py-4 lg:overflow-y-auto">
          {clippings.isLoading ? (
            <p className="text-[15px] text-ink2">Reading your clippings.</p>
          ) : shown.length === 0 ? (
            <div className="max-w-[420px]">
              <EmptyClippings onOpenLatest={() => navigate('/browse')} />
            </div>
          ) : (
            <div className="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {shown.map((row) => (
                <ClippingCard key={row.id} clipping={row} />
              ))}
            </div>
          )}
        </div>
      </div>

      <CitationExport clippings={shown} productName={productName} exportRights={exportRights} />
    </div>
  )
}

function ProjectRow({
  to,
  name,
  count,
  selected,
}: {
  to: string
  name: string
  count: number
  selected: boolean
}) {
  return (
    <Link
      to={to}
      className={`flex items-center gap-2.5 rounded-[--radius-md] px-2.5 py-[7px] no-underline ${
        selected ? 'bg-paper text-body' : 'text-body hover:bg-paper'
      }`}
      style={selected ? { boxShadow: 'inset 0 0 0 1px var(--jl-accent)' } : undefined}
    >
      <span
        className={`min-w-0 flex-1 truncate text-[14px] ${selected ? 'font-semibold' : ''}`}
      >
        {name}
      </span>
      <span className="font-mono text-[10px] text-ink2">{count}</span>
    </Link>
  )
}

/**
 * One clipping: the piece of newsprint itself, what it is, where it came from,
 * and the note the researcher made. The crop is served by the application
 * rather than object storage, because object storage cannot check a licence,
 * so it can be refused; when it is, the placeholder names the page and region
 * that belong there instead of drawing a broken image.
 */
function ClippingCard({ clipping }: { clipping: ClippingRowPlus }) {
  const [failed, setFailed] = useState(false)
  const citation = clipping.citation as CitationRecord
  const date = clipping.pub_date ?? citation.date ?? null
  const label = clipping.section_label ?? citation.page ?? null
  const low = clipping.confidence !== null && clipping.confidence !== undefined && clipping.confidence < 0.85

  return (
    <article className="overflow-hidden rounded-[--radius-md] border border-n300 bg-paper">
      {/*
        A tall region would otherwise make one card three times the height of
        its neighbours and tear the grid apart, so the crop is given a ceiling
        and shows its top. Nothing is scaled or squeezed: a clipping is
        evidence and the card is a way in to it, not a substitute for it.
      */}
      <Link
        to={`/page/${clipping.page_id}${qs({ r: clipping.region })}`}
        className="block max-h-[230px] overflow-hidden bg-well"
      >
        {failed ? (
          <CropPlaceholder
            className="h-[150px] w-full"
            lines={['page crop', label ? `page ${label}` : 'page', clipping.region]}
          />
        ) : (
          <img
            src={clipping.crop_url ?? regionUrl(clipping.page_id, clipping.region, 250)}
            alt={`Clipping from page ${label ?? clipping.page_number ?? ''}`}
            loading="lazy"
            onError={() => setFailed(true)}
            className="block w-full"
          />
        )}
      </Link>
      <div className="px-3 pb-3 pt-2.5">
        <div className="mb-1.5 flex items-center gap-[7px]">
          <TypeBadge type={(clipping.unit_type as UnitType | null) ?? 'article'} />
          <div className="font-mono text-[9.5px] text-ink2">
            {[date, label].filter(Boolean).join(' · ')}
          </div>
          {low && (
            <div className="ml-auto font-mono text-[9px] uppercase text-mark">Low conf</div>
          )}
        </div>
        <h3 className="line-clamp-3 text-[15.5px] font-semibold leading-[1.25]">
          {citation.headline || clipping.headline || 'Untitled passage'}
        </h3>
        {clipping.note && (
          <p className="mt-1.5 text-[13.5px] italic leading-[1.35] text-ink2">{clipping.note}</p>
        )}
      </div>
    </article>
  )
}

// --- the citation panel ----------------------------------------------------

function CitationExport({
  clippings,
  productName,
  exportRights,
}: {
  clippings: ClippingRowPlus[]
  productName: string
  exportRights: boolean
}) {
  const [style, setStyle] = useState<CitationStyle>('chicago')
  const records = clippings.map((row) => row.citation as CitationRecord)
  const rendered = records.map((record, index) =>
    renderStyle(record as CitationRecord & Record<string, unknown>, style, {
      productName,
      index,
    }),
  )
  const mono = style === 'bibtex' || style === 'ris' || style === 'csl-json'
  // Where every record carried the API's own rendering, say so; where any fell
  // through to the client-side composition, say that instead.
  const fromServer =
    records.length > 0 &&
    records.every((record) =>
      isServerRendered(record as CitationRecord & Record<string, unknown>, style),
    )

  return (
    <aside
      id="citation-export"
      className="flex w-full flex-none flex-col border-t border-n300 lg:min-h-0 xl:w-[392px] xl:border-l xl:border-t-0"
    >
      <div className="flex flex-none items-center gap-2 border-b border-n300 px-[18px] py-3.5">
        <div className="chrome">Citation export</div>
        <div className="ml-auto font-mono text-[10px] uppercase text-ink2">
          {clippings.length} selected
        </div>
      </div>
      <div className="flex-none border-b border-n300 px-[18px] py-[13px]">
        <div className="flex flex-wrap gap-[5px]">
          {CITATION_STYLES.map((option) => {
            const on = option.value === style
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={on}
                onClick={() => setStyle(option.value)}
                className="rounded-[--radius-md] px-2.5 py-1 text-[12.5px]"
                style={
                  on
                    ? { background: 'var(--jl-accent)', color: 'var(--color-bg)' }
                    : { border: '1px solid var(--color-neutral-400)' }
                }
              >
                {option.label}
              </button>
            )
          })}
        </div>
      </div>
      <div className="min-h-0 flex-1 px-[18px] py-3.5 lg:overflow-y-auto">
        <div className="mb-2.5 font-mono text-[9.5px] uppercase tracking-[0.12em] text-ink2">
          {STYLE_NOTE[style]}
        </div>
        {rendered.length === 0 ? (
          <p className="text-[14px] leading-normal text-ink2">
            Nothing to cite yet. A clipping arrives here with its citation already attached.
          </p>
        ) : (
          <div className="flex flex-col gap-[13px]">
            {rendered.map((text, index) => (
              <div
                key={clippings[index].id}
                className={
                  mono
                    ? 'whitespace-pre-wrap break-words border-b border-n200 pb-[13px] font-mono text-[11.5px] leading-[1.6] last:border-b-0'
                    : 'border-b border-n200 pb-[13px] text-[14.5px] leading-[1.5] last:border-b-0'
                }
              >
                {mono ? text : withItalicPublication(text, records[index].publication ?? null)}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="flex-none border-t border-n300 px-[18px] py-[13px]">
        <div className="mb-2.5 flex gap-1.5">
          <button
            type="button"
            className="btn-primary flex-1 px-3 py-[7px] text-[13.5px]"
            disabled={rendered.length === 0}
            onClick={() => void navigator.clipboard?.writeText(rendered.join('\n\n'))}
          >
            Copy all
          </button>
          <button
            type="button"
            className="btn-secondary px-3 py-[7px] text-[13.5px] font-normal disabled:opacity-45"
            disabled={!exportRights || records.length === 0}
            onClick={() =>
              download(
                'clippings.bib',
                'application/x-bibtex',
                records
                  .map((record, index) =>
                    renderStyle(record as CitationRecord & Record<string, unknown>, 'bibtex', {
                      productName,
                      index,
                    }),
                  )
                  .join('\n\n'),
              )
            }
          >
            .bib
          </button>
          <button
            type="button"
            className="btn-secondary px-3 py-[7px] text-[13.5px] font-normal disabled:opacity-45"
            disabled={!exportRights || records.length === 0}
            onClick={() =>
              download(
                'clippings.ris',
                'application/x-research-info-systems',
                records
                  .map((record, index) =>
                    renderStyle(record as CitationRecord & Record<string, unknown>, 'ris', {
                      productName,
                      index,
                    }),
                  )
                  .join('\n'),
              )
            }
          >
            .ris
          </button>
          <button
            type="button"
            className="btn-secondary px-3 py-[7px] text-[13.5px] font-normal disabled:opacity-45"
            disabled={!exportRights || records.length === 0}
            onClick={() => download('clippings.csv', 'text/csv', recordsToCsv(records))}
          >
            .csv
          </button>
        </div>
        <div className="font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
          Every citation was frozen when its clipping was saved.
          <br />
          Re-ingest and enrichment cannot move a published footnote.
          {records.length > 0 && (
            <>
              <br />
              {fromServer
                ? 'Rendered by the archive beside the frozen record.'
                : 'Composed here from the frozen record, as a fallback.'}
            </>
          )}
        </div>
      </div>
    </aside>
  )
}

/** The publication is italic in every prose style, and it is the only italic. */
function withItalicPublication(text: string, publication: string | null): ReactNode {
  if (!publication) return text
  const at = text.indexOf(publication)
  if (at < 0) return text
  return (
    <>
      {text.slice(0, at)}
      <em>{publication}</em>
      {text.slice(at + publication.length)}
    </>
  )
}

function download(name: string, mime: string, body: string) {
  const url = URL.createObjectURL(new Blob([body], { type: `${mime};charset=utf-8` }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  anchor.click()
  URL.revokeObjectURL(url)
}

function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'clippings'
}

/** "2 hours ago", for the one line on this screen that wants it. */
function relative(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 90) return 'just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} minutes ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${days} day${days === 1 ? '' : 's'} ago`
}

// --- 05.5 saved searches and alerts ---------------------------------------

/**
 * A saved search re-runs on a schedule and tells you only what is new, which
 * is what makes it useful while the archive is still being ingested beneath
 * the reader.
 *
 * `result_count` and `new_count` are the API's own figures, recorded against
 * the saved search rather than recomputed here, so the "N NEW" badge is a
 * claim the server stands behind. Where a row carries no count the line says
 * so rather than inventing one.
 */
export function SavedSearchesPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [frequency, setFrequency] = useState<AlertFrequency>('weekly')
  const [open, setOpen] = useState<string | null>(null)

  const saved = useQuery({
    queryKey: ['saved-searches'],
    queryFn: () => api.get<SavedSearchRow[]>('/saved-searches'),
  })

  const rows = saved.data ?? []

  const remove = useMutation({
    mutationFn: (id: string) => api.del<void>(`/saved-searches/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['saved-searches'] }),
  })

  const setAlert = useMutation({
    mutationFn: (input: { id: string; frequency: AlertFrequency }) =>
      api.patch<SavedSearchRow>(`/saved-searches/${input.id}`, {
        alert_frequency: input.frequency,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['saved-searches'] }),
  })

  return (
    <div className="mx-auto w-full max-w-[720px] px-4 py-6 md:px-6">
      <div className="mb-3 flex items-baseline gap-2.5">
        <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent">5.5</div>
        <h1 className="chrome">Saved searches &amp; alerts</h1>
      </div>

      <ErrorNote error={remove.error} />
      {setAlert.isError && (
        <p className="mb-3 font-mono text-[10px] uppercase leading-[1.7] text-mark">
          The alert schedule could not be changed: the saved-search API has no update endpoint yet.
          The stored query is unaffected.
        </p>
      )}

      {saved.isLoading ? (
        <p className="text-[15px] text-ink2">Reading your saved searches.</p>
      ) : rows.length === 0 ? (
        <div className="max-w-[420px]">
          <EmptySavedSearches
            frequency={frequency}
            onFrequency={setFrequency}
            onSaveLast={() => navigate('/search')}
          />
        </div>
      ) : (
        <div className="overflow-hidden rounded-[--radius-md] border border-rule2 bg-paper">
          {rows.map((row) => {
            const params = toParams(row.query)
            const semantic = String(params.mode ?? '') === 'semantic'
            const total = row.result_count ?? null
            const current: AlertFrequency =
              row.alert_frequency ?? (row.alert_enabled ? 'weekly' : 'off')
            const isOpen = open === row.id
            return (
              <Fragment key={row.id}>
                <div className="flex items-center gap-3 border-b border-n300 px-4 py-3 last:border-b-0">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex items-center gap-[7px]">
                      {semantic && (
                        <span
                          className="font-mono text-[8.5px] uppercase tracking-[0.1em] text-accent"
                          style={{ border: '1px dashed var(--jl-accent)', padding: '0 4px' }}
                        >
                          Semantic
                        </span>
                      )}
                      <div className="truncate font-mono text-[12.5px]">{row.name}</div>
                    </div>
                    <button
                      type="button"
                      className="flex items-center gap-1 font-mono text-[9.5px] uppercase text-ink2 hover:text-accent"
                      aria-expanded={isOpen}
                      onClick={() => setOpen(isOpen ? null : row.id)}
                    >
                      {total === null ? 'not counted yet' : `${total.toLocaleString()} results`} ·
                      alert {current}
                      <Chevron direction={isOpen ? 'up' : 'down'} size={10} />
                    </button>
                  </div>
                  {row.new_count ? (
                    <div
                      className="flex-none rounded-[--radius-md] px-1.5 py-0.5 font-mono text-[10px] uppercase"
                      style={{ background: 'var(--jl-accent)', color: 'var(--color-bg)' }}
                    >
                      {row.new_count} new
                    </div>
                  ) : null}
                  <Link
                    to={`/search${qs(params)}`}
                    className="flex-none text-[13px] text-accent no-underline"
                  >
                    Run
                  </Link>
                </div>
                {isOpen && (
                  <div className="border-b border-n300 bg-surface px-4 py-3 last:border-b-0">
                    <AlertRadios
                      name={`alert-${row.id}`}
                      value={current}
                      onChange={(next) => setAlert.mutate({ id: row.id, frequency: next })}
                    />
                    <div className="mt-3 flex items-center gap-3">
                      <button
                        type="button"
                        className="btn-danger px-3 py-1 text-[13px]"
                        onClick={() => remove.mutate(row.id)}
                      >
                        Remove
                      </button>
                      <span className="font-mono text-[9.5px] uppercase text-ink2">
                        {row.last_alerted_at
                          ? `last alerted ${row.last_alerted_at.slice(0, 10)}`
                          : 'never alerted'}
                      </span>
                    </div>
                  </div>
                )}
              </Fragment>
            )
          })}
        </div>
      )}

      <p className="mt-3 font-mono text-[9.5px] uppercase leading-[1.7] text-ink2">
        Counts are the archive's own, recorded against each saved search. Alerts require an email
        address on the account, and email is not configured on this deployment.
      </p>
    </div>
  )
}

/** The stored query, flattened into the search parameters it was saved as. */
function toParams(query: Record<string, unknown>): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === null || value === undefined || value === '') continue
    if (typeof value === 'object') continue
    out[key] = value as string | number | boolean
  }
  return out
}
